import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import PlaygroundChat from './pages/PlaygroundChat';
import DbMonitor from './pages/DbMonitor';
import AgentUsers from './pages/AgentUsers';
import ChatHistory from './pages/ChatHistory';
import Memory from './pages/Memory';
import Documents from './pages/Documents';
import Tiers from './pages/Tiers';
import ApiKeys from './pages/ApiKeys';
import Logs from './pages/Logs';
import Usage from './pages/Usage';
import ImagePresets from './pages/ImagePresets';
import AdminAccounts from './pages/AdminAccounts';
import Settings from './pages/Settings';
import A2UIPreview from './pages/A2UIPreview';
import Login from './pages/Login';
import Register from './pages/Register';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { Page } from './types';
import ErrorBoundary from './components/ErrorBoundary';

const LoadingScreen: React.FC = () => (
  <div className="flex h-screen items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="spinner w-10 h-10 mx-auto mb-4"></div>
      <p className="text-slate-500">Loading...</p>
    </div>
  </div>
);

// Map Page enum to route paths
const pageToPath: Record<Page, string> = {
  [Page.DASHBOARD]: '/',
  [Page.PLAYGROUND]: '/playground',
  [Page.DB_MONITOR]: '/db-monitor',
  [Page.USERS]: '/users',
  [Page.CHATS]: '/chats',
  [Page.MEMORY]: '/memory',
  [Page.DOCUMENTS]: '/documents',
  [Page.TIERS]: '/tiers',
  [Page.API_KEYS]: '/api-keys',
  [Page.IMAGE_PRESETS]: '/image-presets',
  [Page.USAGE]: '/usage',
  [Page.LOGS]: '/logs',
  [Page.ADMIN_ACCOUNTS]: '/admin-accounts',
  [Page.SETTINGS]: '/settings',
};

// Map route paths to Page enum
const pathToPage: Record<string, Page> = {
  '/': Page.DASHBOARD,
  '/playground': Page.PLAYGROUND,
  '/db-monitor': Page.DB_MONITOR,
  '/users': Page.USERS,
  '/chats': Page.CHATS,
  '/memory': Page.MEMORY,
  '/documents': Page.DOCUMENTS,
  '/tiers': Page.TIERS,
  '/api-keys': Page.API_KEYS,
  '/image-presets': Page.IMAGE_PRESETS,
  '/usage': Page.USAGE,
  '/logs': Page.LOGS,
  '/admin-accounts': Page.ADMIN_ACCOUNTS,
  '/settings': Page.SETTINGS,
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current page from URL path
  const getCurrentPage = (): Page => {
    const path = location.pathname;
    // Handle paths that start with /playground (including /playground?session=xxx)
    if (path.startsWith('/playground')) return Page.PLAYGROUND;
    return pathToPage[path] || Page.DASHBOARD;
  };

  const currentPage = getCurrentPage();

  const handleNavigate = (page: Page) => {
    navigate(pageToPath[page]);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  if (mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      <Sidebar currentPage={currentPage} onNavigate={handleNavigate} />

      <div className="flex-1 flex flex-col min-w-0">
        <Header />

        <main className="flex-1 overflow-y-auto p-6 scroll-smooth">
          <div className="h-full">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/playground" element={<PlaygroundChat />} />
                <Route path="/db-monitor" element={<DbMonitor />} />
                <Route path="/users" element={<AgentUsers />} />
                <Route path="/chats" element={<ChatHistory />} />
                <Route path="/memory" element={<Memory />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/tiers" element={<Tiers />} />
                <Route path="/api-keys" element={<ApiKeys />} />
                <Route path="/usage" element={<Usage />} />
                <Route path="/logs" element={<Logs />} />
                <Route path="/image-presets" element={<ImagePresets />} />
                <Route path="/admin-accounts" element={<AdminAccounts />} />
                <Route path="/settings" element={<Settings />} />
                {/* Dev-only A2UI render preview harness — URL-accessible,
                    no sidebar entry. Shares A2UISurfaceRenderer with the live views. */}
                <Route path="/a2ui-preview" element={<A2UIPreview />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
